import {
  type NotificationDelivery,
  type NotificationPreferenceUpsert,
  NotificationsService,
} from "@common/client"
import { useMutationState } from "@tanstack/react-query"
import { makeAxiosMutation, makeNoArgsQuery } from "./utils"

export const notificationsKey = ["notifications", "unseen"] as const

export const notificationPreferencesKey = [
  "notifications",
  "preferences",
] as const

const updateNotificationPreferencesKey = [
  ...notificationPreferencesKey,
  "update",
] as const

export const notificationsQuery = makeNoArgsQuery({
  serviceCall: NotificationsService.listNotifications,
  queryKey: notificationsKey,
})

export const notificationPreferencesQuery = makeNoArgsQuery({
  serviceCall: NotificationsService.listNotificationPreferences,
  queryKey: notificationPreferencesKey,
})

export const useMarkNotificationSeenMutation = makeAxiosMutation({
  serviceCall: NotificationsService.markSeen,
  invalidates: () => [notificationsKey],
})

export const useMarkAllNotificationsSeenMutation = makeAxiosMutation({
  serviceCall: (_: undefined) => NotificationsService.markAllSeen(),
  invalidates: () => [notificationsKey],
})

export const useResolveNotificationTraceMutation = makeAxiosMutation({
  serviceCall: NotificationsService.resolveTrace,
  invalidates: () => [notificationsKey],
})

export const useUpdateNotificationPreferencesMutation = makeAxiosMutation({
  serviceCall: NotificationsService.updateNotificationPreferences,
  invalidates: () => [notificationPreferencesKey],
  mutationKey: updateNotificationPreferencesKey,
  scope: { id: "notification-preferences" },
})

function isPreferencesUpdate(
  variables: unknown,
): variables is { body: NotificationPreferenceUpsert[] } {
  return (
    typeof variables === "object" &&
    variables !== null &&
    "body" in variables &&
    Array.isArray(variables.body)
  )
}

export type PendingDeliveries = Map<string, NotificationDelivery>

export const usePendingNotificationPreferences = (): PendingDeliveries =>
  new Map(
    useMutationState({
      filters: {
        mutationKey: updateNotificationPreferencesKey,
        status: "pending",
      },
      select: (mutation) => mutation.state.variables,
    })
      .flatMap((variables) =>
        isPreferencesUpdate(variables) ? variables.body : [],
      )
      .map(({ rule_id, delivery }) => [rule_id, delivery]),
  )
